import { Box, Stack } from '@mui/material';
import { useMemo } from 'react';
import { FieldValues, useWatch } from 'react-hook-form';

import {
  AdditionalItemRendererProps,
  FormAwareElementProps,
  FormAwareInputProps,
  SuggestionLocatorPosition,
} from '@/components/form/formAwareInputs/FormAwareInput.types';
import { LABEL_OFFSET_HEIGHT } from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputFieldItem';
import { useFormContext } from '@/components/react-hook-form';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { SuggestionForSearchAndEvidence } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import {
  FormAwareLocateSuggestionButton,
  FormAwareLocateSuggestionButtonVariant,
} from '@/modules/aiSuggestions/components/forms/FormAwareLocateSuggestionButton';
import { useTestamentaryEntitySuggestionsContextIfAvailable } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.context';

import { useEntitySuggestionsContext } from '../../context/EntitySuggestions.context';

interface LocatableFormAwareInputProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> {
  suggestionKind: MatchableKind | null;
  additionalItemRendererProps?: AdditionalItemRendererProps;
  FormAwareElement: FormAwareElementProps<S, T>;
}

type LocatableFormAwareInputCombinedProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> = LocatableFormAwareInputProps<S, T> & T;

/**
 * A wrapper around a FormAwareInput that shows a locator in the field's label.
 */
export function LocatableFormAwareInput<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
>(props: LocatableFormAwareInputCombinedProps<S, T>) {
  const {
    fieldName,
    suggestionKind,
    FormAwareElement,
    additionalItemRendererProps,
  } = props;

  const { getMatchableSuggestion: getEntitySuggestion } =
    useEntitySuggestionsContext();

  const { getMatchableSuggestion: getDPSuggestion } =
    useDispositiveProvisionSuggestionsContext();

  // Subtrust modal mounts a TestamentaryEntitySuggestionsProvider instead of
  // the entity / DP providers — read from it when present.
  const testamentaryEntitySuggestionsContext =
    useTestamentaryEntitySuggestionsContextIfAvailable();

  const { control } = useFormContext();
  const fieldValue = useWatch({
    name: fieldName,
    control,
  }) as string | undefined;

  // Check if the locator icon should be on the right side of the input,
  // instead of as the endAdornment of the label.
  // We use this when rendering the suggested input field in the DuplicatedInputFieldGroup.
  const isIconPositionRight = useMemo(
    () =>
      additionalItemRendererProps?.locatorPosition ===
      SuggestionLocatorPosition.RIGHT,
    [additionalItemRendererProps?.locatorPosition]
  );

  const getTestamentaryEntitySuggestion =
    testamentaryEntitySuggestionsContext?.getMatchableSuggestion;

  const suggestion = useMemo<SuggestionForSearchAndEvidence | undefined>(() => {
    if (!suggestionKind) return undefined;
    const entitySuggestion = getEntitySuggestion(
      suggestionKind,
      fieldValue || ''
    );
    const dpSuggestion = getDPSuggestion(suggestionKind, fieldValue || '');
    const testamentaryEntitySuggestion = getTestamentaryEntitySuggestion?.(
      suggestionKind,
      fieldValue || ''
    );
    return entitySuggestion || dpSuggestion || testamentaryEntitySuggestion;
  }, [
    fieldValue,
    getDPSuggestion,
    getEntitySuggestion,
    getTestamentaryEntitySuggestion,
    suggestionKind,
  ]);

  const locatorIcon = useMemo(() => {
    if (
      additionalItemRendererProps?.hideLocateSuggestionIconOverride &&
      isIconPositionRight
    ) {
      // Render a placeholder in place of the locator icon if the suggestion is
      // hidden and the locator icon is on the right.
      return <Box width={20} height={20} />;
    }

    return (
      <FormAwareLocateSuggestionButton
        suggestion={suggestion}
        variant={FormAwareLocateSuggestionButtonVariant.FIELD}
        additionalItemRendererProps={additionalItemRendererProps}
        sx={{
          mb: props.hideLabel ? 0.5 : 0,
          ml: isIconPositionRight ? 0 : undefined,
        }}
      />
    );
  }, [
    additionalItemRendererProps,
    isIconPositionRight,
    props.hideLabel,
    suggestion,
  ]);

  return (
    <FormAwareElement
      {...props}
      hideLabel={props.hideLabel}
      label={props.label}
      labelIconEnd={!isIconPositionRight && locatorIcon}
      rightLocatorIcon={
        isIconPositionRight && (
          <Stack direction="column">
            <Box height={LABEL_OFFSET_HEIGHT} />
            {locatorIcon}
          </Stack>
        )
      }
    />
  );
}
